import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Leaf, Award, Recycle, ShieldCheck, Heart } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const SuppliersPage = () => {
  const { language } = useLanguage();

  const advantages = [
    {
      icon: <Heart className="w-6 h-6 text-accent" />,
      text: language === 'PL' ? "Od 3 pokoleń doskonalą jakość swoich produktów" : "Perfecting the quality of their products for 3 generations"
    },
    {
      icon: <Leaf className="w-6 h-6 text-accent" />,
      text: language === 'PL' ? "Produkują ekologicznie" : "Eco-friendly production"
    },
    {
      icon: <Award className="w-6 h-6 text-accent" />,
      text: language === 'PL' ? "Tworzą produkty, które cechują się jakością i zgodnością z tradycją regionu Apulii" : "Creating products characterized by quality and compliance with the Apulia region's traditions"
    },
    {
      icon: <Recycle className="w-6 h-6 text-accent" />,
      text: language === 'PL' ? "Dbają o zrównoważony rozwój i ochronę planety" : "Committed to sustainable development and protecting the planet"
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-accent" />,
      text: language === 'PL' ? "Spełniają najbardziej restrykcyjne normy jakościowe, potwierdzone pełną certyfikacją" : "Meeting the strictest quality standards, confirmed by full certification"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title={language === 'PL' ? "Nasi dostawcy - Włoska Piwnica" : "Our Suppliers - Włoska Piwnica"}
        description={language === 'PL' ? "Poznaj naszych zaufanych włoskich dostawców. Tradycja, ekologia i najwyższa jakość prosto z Apulii." : "Meet our trusted Italian suppliers. Tradition, ecology, and the highest quality straight from Apulia."}
        canonicalUrl="/dostawcy"
      />
      <Header />
      
      <main className="flex-grow pt-36 md:pt-40 lg:pt-44 pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-primary mb-6">
              {language === 'PL' ? "Nasi dostawcy" : "Our Suppliers"}
            </h1>
            <div className="h-1 w-24 bg-accent mx-auto rounded-full mb-8" />
            
            <p className="text-base sm:text-xl text-foreground/80 font-serif max-w-3xl mx-auto leading-relaxed">
              {language === 'PL' ? "Nasi dostawcy to zaufane firmy rodzinne, dla których tradycja i jakość są najwyższym priorytetem. Współpracujemy tylko z najlepszymi, aby dostarczać autentyczne smaki prosto z Włoch." : "Our suppliers are trusted family businesses for whom tradition and quality are the highest priorities. We collaborate only with the best to deliver authentic flavors straight from Italy."}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="text-2xl font-bold text-primary mb-6">{language === 'PL' ? "Co wyróżnia naszych dostawców?" : "What distinguishes our suppliers?"}</h2>
              
              <ul className="space-y-5">
                {advantages.map((item, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="mt-1 bg-primary/5 p-2 rounded-lg border border-primary/10">
                      {item.icon}
                    </div>
                    <span className="text-lg text-foreground/80 leading-snug pt-1">
                      {item.text}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative h-full min-h-[280px] sm:min-h-[400px] rounded-2xl overflow-hidden border border-border shadow-xl glass-effect p-8 flex flex-col items-center justify-center gap-8"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/10 -z-10" />
              
              <div className="grid grid-cols-2 gap-8 w-full items-center justify-items-center">
                <Link to="/dostawcy/tenuta-vigilione" className="transition-transform hover:scale-105" aria-label="Tenuta Viglione">
                  <img 
                    src="/suppliers/vigilione/logo.webp" 
                    alt="Tenuta Viglione Logo" 
                    loading="lazy"
                    width="128" height="128"
                    className="max-h-24 md:max-h-32 object-contain filter drop-shadow-md"
                  />
                </Link>
                <Link to="/dostawcy/fiore-di-puglia" className="transition-transform hover:scale-105" aria-label="Fiore di Puglia">
                  <img 
                    src="/suppliers/fiore/logo.webp" 
                    alt="Fiore di Puglia Logo" 
                    loading="lazy"
                    width="128" height="128"
                    className="max-h-24 md:max-h-32 object-contain filter drop-shadow-md"
                  />
                </Link>
                <Link to="/dostawcy/galantino" className="transition-transform hover:scale-105" aria-label="Galantino">
                  <img 
                    src="/suppliers/galantino/logo.webp" 
                    alt="Frantoio Galantino Logo" 
                    loading="lazy"
                    width="128" height="128"
                    className="max-h-24 md:max-h-32 object-contain filter drop-shadow-md"
                  />
                </Link>
                <div className="transition-transform hover:scale-105">
                  <img 
                    src="/suppliers/italy-flag.webp" 
                    alt="Flaga Włoch" 
                    loading="lazy"
                    width="128" height="128"
                    className="max-h-20 md:max-h-24 object-contain filter drop-shadow-md rounded-sm"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SuppliersPage;
